import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Inbox, AlertCircle, Clock, MessageSquare, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import ActivityLog from "./admin/ActivityLog";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "contacted", label: "Contacted" },
  { value: "closed", label: "Closed" },
];

const URGENCY_BADGE = {
  routine: "bg-slate-100 text-slate-700",
  soon: "bg-blue-50 text-[#3B597B]",
  urgent: "bg-amber-100 text-amber-800",
  crisis: "bg-red-100 text-red-800",
};

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterUrgency, setFilterUrgency] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status_filter = filterStatus;
      if (filterUrgency) params.urgency = filterUrgency;
      const [r, s] = await Promise.all([
        api.get("/admin/support-requests", { params }),
        api.get("/admin/stats"),
      ]);
      setRequests(r.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterUrgency]);

  useEffect(() => { load(); }, [load]);

  // Fetch full detail (with activities) whenever a request is selected
  useEffect(() => {
    if (!selected?.id) { setDetail(null); return; }
    let ignore = false;
    setDetailLoading(true);
    api
      .get(`/admin/support-requests/${selected.id}`)
      .then((r) => { if (!ignore) setDetail(r.data); })
      .catch((err) => { if (!ignore) toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); })
      .finally(() => { if (!ignore) setDetailLoading(false); });
    return () => { ignore = true; };
  }, [selected?.id]);

  const closeDrawer = () => { setSelected(null); setDetail(null); };

  const suggestContacted = useCallback(() => {
    toast("Move status to Contacted?", {
      description: "This request is currently " + (detail?.status || selected?.status || ""),
      action: {
        label: "Yes, mark Contacted",
        onClick: () => {
          if (selected?.id) updateStatus(selected.id, "contacted");
        },
      },
      duration: 8000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.status, selected]);

  const handleLogout = async () => {
    await logout();
    navigate("/care-portal/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await api.patch(`/admin/support-requests/${id}/status`, { status });
      setRequests((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      if (selected?.id === id) setSelected(res.data);
      toast.success("Status updated");
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const KPIS = stats
    ? [
        { label: "Total", value: stats.total, icon: Inbox },
        { label: "New", value: stats.new, icon: AlertCircle },
        { label: "In progress", value: stats.in_progress, icon: Clock },
        { label: "Contacted", value: stats.contacted, icon: MessageSquare },
        { label: "Closed", value: stats.closed, icon: CheckCircle2 },
      ]
    : [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-2">Admin dashboard</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">Support requests</h1>
          <p className="mt-2 text-sm text-slate-500">Signed in as {user?.email}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="dashboard-refresh"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5] transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={handleLogout}
            data-testid="dashboard-logout"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {KPIS.map((k) => (
          <div key={k.label} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
              <k.icon className="w-3.5 h-3.5" /> {k.label}
            </div>
            <div className="mt-3 font-heading text-3xl text-slate-900">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="dashboard-filters">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Status</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]">
            <option value="">All</option>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Urgency</span>
          <select value={filterUrgency} onChange={(e) => setFilterUrgency(e.target.value)} data-testid="filter-urgency" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]">
            <option value="">All</option>
            <option value="routine">Routine</option>
            <option value="soon">Soon</option>
            <option value="urgent">Urgent</option>
            <option value="crisis">Crisis</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {renderRequestsBody({ loading, requests, updateStatus, setSelected })}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="request-detail-drawer">
          <div className="flex-1 bg-slate-900/30" onClick={closeDrawer} />
          <div className="w-full max-w-2xl bg-white border-l border-slate-200 overflow-y-auto p-6 md:p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-2">Request</p>
                <h2 className="font-heading text-2xl text-slate-900">{selected.first_name} {selected.last_name}</h2>
              </div>
              <button onClick={closeDrawer} className="text-slate-500 hover:text-slate-900" data-testid="close-drawer">✕</button>
            </div>
            <dl className="space-y-4 text-sm">
              <Row label="Company" value={selected.company_name} />
              <Row label="Email" value={selected.email} />
              <Row label="Phone" value={selected.phone} />
              <Row label="Preferred contact" value={selected.preferred_contact} />
              <Row label="Best time" value={selected.best_time_to_contact} />
              <Row label="Urgency" value={selected.urgency} />
              <Row label="Status" value={(detail?.status || selected.status)} />
              <div>
                <dt className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium mb-1.5">Reason</dt>
                <dd className="text-slate-700 leading-relaxed bg-[#F3F5F7] rounded-lg p-3.5">{selected.reason}</dd>
              </div>
              <Row label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
            </dl>

            <ActivityLog
              requestId={selected.id}
              detail={detail || { status: selected.status, activities: [] }}
              currentUser={user}
              onUpdated={(updated) => {
                setDetail(updated);
                // Keep list row in sync if any top-level field changed (status remains authoritative on server)
                setRequests((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...{ status: updated.status } } : r)));
                if (selected?.id === updated.id) setSelected({ ...selected, status: updated.status });
              }}
              onStatusSuggest={suggestContacted}
            />

            {detailLoading && (
              <p className="mt-4 text-xs text-slate-400" data-testid="detail-loading">Loading activity log…</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">{label}</dt>
      <dd className="text-slate-800 text-right">{value}</dd>
    </div>
  );
}

function renderRequestsBody({ loading, requests, updateStatus, setSelected }) {
  if (loading) {
    return <div className="p-12 text-center text-slate-500 text-sm">Loading...</div>;
  }
  if (requests.length === 0) {
    return (
      <div className="p-12 text-center text-slate-500 text-sm" data-testid="empty-requests">
        No support requests yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-[0.16em] text-slate-500">
          <tr>
            <th className="text-left px-5 py-3 font-medium">Name</th>
            <th className="text-left px-5 py-3 font-medium">Company</th>
            <th className="text-left px-5 py-3 font-medium">Urgency</th>
            <th className="text-left px-5 py-3 font-medium">Status</th>
            <th className="text-left px-5 py-3 font-medium">Submitted</th>
            <th className="text-left px-5 py-3 font-medium"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {requests.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`request-row-${r.id}`}>
              <td className="px-5 py-4 text-slate-900 font-medium">{r.first_name} {r.last_name}</td>
              <td className="px-5 py-4 text-slate-600">{r.company_name}</td>
              <td className="px-5 py-4">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${URGENCY_BADGE[r.urgency] || "bg-slate-100 text-slate-700"}`}>
                  {r.urgency}
                </span>
              </td>
              <td className="px-5 py-4">
                <select
                  value={r.status}
                  onChange={(e) => updateStatus(r.id, e.target.value)}
                  data-testid={`status-select-${r.id}`}
                  className="rounded-md border border-slate-300 px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
                >
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </td>
              <td className="px-5 py-4 text-slate-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
              <td className="px-5 py-4">
                <button
                  onClick={() => setSelected(r)}
                  data-testid={`view-request-${r.id}`}
                  className="text-[#4A6FA5] hover:text-[#3B597B] text-sm font-medium"
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
